import { BadRequest, InternalError } from '@systemeio/util'
import { PRODUCTS_API, createProduct } from 'modules/products/api/product-api'
import ProductForm from 'modules/products/components/product-form'
import { emptyProductErrorsState } from 'modules/products/constants/product-errors'
import { ProductsLoco } from 'modules/products/constants/products-loco'
import { useMaxVariants } from 'modules/products/hooks/use-max-variants'
import { useProductDefaultValues } from 'modules/products/hooks/use-products'
import { ProductErrorResponseContentInterface } from 'modules/products/types/error-response-content-interface'
import { ProductSaveInterface } from 'modules/products/types/product-interface'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { Loader } from 'shared/components/loader'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const CreateProduct: NextPage = () => {
  const { data: defaultData } = useProductDefaultValues()
  const { maxVariants } = useMaxVariants()
  const [isFetching, setIsFetching] = useState(false)
  const [validationErrorsNotification, setValidationErrorsNotification] = useState(false)
  const { t } = useLocoTranslation()

  const updateProductListData = useUpdateData({
    api: PRODUCTS_API,
  })

  const router = useRouter()

  const handleCreateProduct = async (productSaveData: ProductSaveInterface) => {
    setIsFetching(true)

    const { productData, setErrors, setVariantErrors } = productSaveData

    try {
      setErrors(emptyProductErrorsState)
      setValidationErrorsNotification(false)
      await createProduct(productData)
      setIsFetching(false)
      await updateProductListData(true, {})
      await router.push('/products')
    } catch (e) {
      setIsFetching(false)
      if (e instanceof BadRequest) {
        const productErrors = e.errors as unknown as ProductErrorResponseContentInterface
        setErrors({
          name: productErrors.fields.name?.join(''),
          currency: productErrors.fields.currency?.join(''),
          price: productErrors.fields.price?.join(''),
          sku: productErrors.fields.sku?.join(''),
          tax: productErrors.fields.tax?.join(''),
          weight: productErrors.fields.weight?.join(''),
          common: productErrors.common,
          options: (productErrors.fields.options as Record<string, Record<string, string[]>>) || {},
          stock: productErrors.fields.stock?.join(''),
          shippingFeesDisabled: productErrors.fields.shippingFeesDisabled?.join(''),
          taxBehavior: productErrors.fields.taxBehavior?.join(''),
        })

        if (!!productErrors.fields?.productVariants) {
          setVariantErrors(productErrors.fields.productVariants)
        }

        if (!!Object.values(productErrors.fields).length) {
          setValidationErrorsNotification(true)
        }
      } else if (e instanceof InternalError) {
        setErrors(prev => {
          return {
            ...prev,
            common: [t('core.error.internal_error_message')],
          }
        })
      }
    }
  }

  if (!maxVariants) {
    return (
      <div className="m-auto table">
        <Loader type="dark" />
      </div>
    )
  }

  return (
    <ProductForm
      maxProductVariants={maxVariants.maxProductVariants}
      onSave={handleCreateProduct}
      setValidationErrorsNotification={setValidationErrorsNotification}
      validationErrorsNotification={validationErrorsNotification}
      defaultData={defaultData}
      isFetching={isFetching}
    />
  )
}

export const getServerSideProps = getBaseServerSideProps(
  ProductsLoco,
  'header.top_menu.physical_product',
)

export default CreateProduct
